// Central error log collector.
//
// Goals:
//  - Collect errors from the right places: React (via ErrorBoundary), global
//    window errors + unhandled promise rejections, and service catch blocks
//    (midi / ai / firebase / profile) which call errorLog.capture(...).
//  - Be exportable: serialize the collected log and download it as JSON.
//  - Be node-safe so it can be unit-tested without a browser (all window/
//    document/Blob usage is guarded).
//
// It deliberately does NOT log secrets. Callers pass an error + a small context
// object; do not put tokens/keys/PII in the context.

/* global __APP_VERSION__ */
const APP_VERSION =
  typeof __APP_VERSION__ !== 'undefined' ? __APP_VERSION__ : 'dev';

const MAX_ENTRIES = 500;

class ErrorLog {
  constructor() {
    this.entries = [];
    this._installed = false;
  }

  /** Low-level append. Returns the stored entry. */
  add({ source = 'app', level = 'error', message = '', stack = '', context = null } = {}) {
    const entry = {
      timestamp: new Date().toISOString(),
      source,
      level,
      message: String(message),
      stack: stack ? String(stack) : '',
      context: context == null ? null : context,
    };
    this.entries.push(entry);
    // Bound memory: keep only the most recent MAX_ENTRIES.
    if (this.entries.length > MAX_ENTRIES) {
      this.entries.splice(0, this.entries.length - MAX_ENTRIES);
    }
    return entry;
  }

  /** Capture a caught Error (or anything) with a source tag + context. */
  capture(error, source = 'app', context = null) {
    const message = (error && error.message) || String(error);
    const stack = (error && error.stack) || '';
    return this.add({ source, level: 'error', message, stack, context });
  }

  warn(message, source = 'app', context = null) {
    return this.add({ source, level: 'warn', message, context });
  }

  info(message, source = 'app', context = null) {
    return this.add({ source, level: 'info', message, context });
  }

  getAll() {
    return this.entries.slice();
  }

  count() {
    return this.entries.length;
  }

  clear() {
    this.entries = [];
  }

  /** Serialize the whole log (with app metadata) to a pretty JSON string. */
  serialize() {
    return JSON.stringify(
      {
        app: 'looper-midi-control',
        version: APP_VERSION,
        exportedAt: new Date().toISOString(),
        userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : 'n/a',
        count: this.entries.length,
        entries: this.entries,
      },
      null,
      2,
    );
  }

  /**
   * Export the log. In a browser this triggers a file download and returns the
   * serialized string; in node (no document/Blob) it just returns the string.
   */
  export(filename) {
    const data = this.serialize();
    if (typeof document === 'undefined' || typeof Blob === 'undefined' || typeof URL === 'undefined') {
      return data;
    }
    const name =
      filename ||
      `looper-midi-control-errorlog-${new Date().toISOString().replace(/[:.]/g, '-')}.json`;
    const blob = new Blob([data], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = name;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    return data;
  }

  /**
   * Install global handlers for uncaught errors + unhandled promise rejections.
   * `target` defaults to window; a stub can be passed for testing.
   */
  installGlobalHandlers(target) {
    const w = target || (typeof window !== 'undefined' ? window : null);
    if (!w || this._installed) return;
    this._installed = true;

    w.addEventListener('error', (e) => {
      this.add({
        source: 'window',
        level: 'error',
        message: (e && e.message) || 'window error',
        stack: (e && e.error && e.error.stack) || '',
        context: e ? { filename: e.filename, lineno: e.lineno, colno: e.colno } : null,
      });
    });

    w.addEventListener('unhandledrejection', (e) => {
      const reason = e && e.reason;
      this.add({
        source: 'promise',
        level: 'error',
        message: (reason && reason.message) || String(reason),
        stack: (reason && reason.stack) || '',
        context: null,
      });
    });
  }
}

const errorLog = new ErrorLog();
export default errorLog;
